import { modifier } from 'ember-modifier';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — NvTabs `@overflow='menu'` (owner 2026-10-22: the patient card tabs must not scroll
 *              sideways; what does not fit goes under «Altro ▾», as the record tabs of the top bar do).
 *              On the wrapper of the strip: measures the tabs, hides (class nv-tablist-tab--off) the ones that
 *              do not fit next to the «Altro» button and reports them to the host component. The selected tab
 *              always stays in the strip: it takes the place of the last one that fits.
 *   usage:     `{{nv-tab-overflow this.setHidden}}` on .nv-tablist-wrap (nv-tabs.hbs); the callback receives
 *              [{ id, label, count }] of the hidden tabs (DOM order), only when the list changes.
 *   uses:      ResizeObserver (the wrapper), MutationObserver (tabs added / removed / relabelled / selected)
 *   gotchas:   (1) the class toggles are NOT observed (attributeFilter aria-selected only): measuring writes
 *              classes and must not wake itself. (2) every pass shows all tabs first and measures them in the
 *              same task, before any paint: no flash. (3) a hidden tab is still the real button: the menu
 *              activates it with .click() (its @onSelect, its gates), never a copy of its logic.
 */
const isSelected = el => el.getAttribute('aria-selected') === 'true' || el.classList.contains('is-selected');
var nvTabOverflow = modifier((wrap, [onChange]) => {
  const strip = wrap.querySelector(':scope > .nv-tablist');
  if (!strip) return undefined;
  const tabs = () => [...strip.children].filter(el => el.classList.contains('nv-tablist-tab'));
  let lastKey = '';
  let frame = 0;
  const measure = () => {
    frame = 0;
    if (!wrap.isConnected) return;
    const all = tabs();
    all.forEach(el => el.classList.remove('nv-tablist-tab--off'));
    wrap.classList.remove('nv-tablist-wrap--more');
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
    const widths = all.map(el => el.offsetWidth);
    const total = widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, all.length - 1);
    let hidden = [];
    if (total > strip.clientWidth + 1) {
      // the «Altro» button takes its room: the strip shrinks to what is left
      wrap.classList.add('nv-tablist-wrap--more');
      const room = strip.clientWidth;
      const visible = [];
      let used = 0;
      for (let i = 0; i < all.length; i++) {
        const w = widths[i] + (visible.length ? gap : 0);
        if (used + w > room) break;
        used += w;
        visible.push(all[i]);
      }
      const selected = all.find(isSelected);
      if (selected && !visible.includes(selected)) {
        const width = el => widths[all.indexOf(el)];
        const sum = () => visible.reduce((s, el, i) => s + width(el) + (i ? gap : 0), 0);
        visible.pop();
        while (visible.length && sum() + gap + width(selected) > room) visible.pop();
        visible.push(selected);
      }
      if (!visible.length && all.length) visible.push(selected ?? all[0]);
      hidden = all.filter(el => !visible.includes(el));
      hidden.forEach(el => el.classList.add('nv-tablist-tab--off'));
    }
    const list = hidden.map(el => ({
      id: el.id,
      label: (el.querySelector('.nv-tablist-text')?.textContent ?? el.textContent ?? '').replace(/\s+/g, ' ').trim(),
      count: el.querySelector('.nv-tablist-count')?.textContent?.trim() || null
    }));
    const key = JSON.stringify(list);
    if (key !== lastKey) {
      lastKey = key;
      onChange?.(list);
    }
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };
  schedule();
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
  resize?.observe(wrap);
  const observer = new MutationObserver(schedule);
  observer.observe(strip, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['aria-selected']
  });
  return () => {
    if (frame) cancelAnimationFrame(frame);
    resize?.disconnect();
    observer.disconnect();
  };
});

export { nvTabOverflow as default };
//# sourceMappingURL=nv-tab-overflow.js.map
